import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { CashDrawerApi } from "../../api/CashDrawerApi";
import type { CashDrawerAddCashRequest } from "../../generated/dto/CashDrawerAddCashRequest";
import type { CashDrawerChangeFetchOptions } from "../../generated/dto/CashDrawerChangeFetchOptions";
import type { CashDrawerChangeRef } from "../../generated/dto/CashDrawerChangeRef";
import type { CashDrawerChangeStaff } from "../../generated/dto/CashDrawerChangeStaff";
import type { CashDrawerDayChangesRequest } from "../../generated/dto/CashDrawerDayChangesRequest";
import type { CashDrawerFetchOptions } from "../../generated/dto/CashDrawerFetchOptions";
import type { CashDrawerOpenDayRequest } from "../../generated/dto/CashDrawerOpenDayRequest";
import type { CashDrawerRef } from "../../generated/dto/CashDrawerRef";
import type { CashDrawerRemoveCashRequest } from "../../generated/dto/CashDrawerRemoveCashRequest";
import type { CashDrawerSaveCountRequest } from "../../generated/dto/CashDrawerSaveCountRequest";
import type { CashDrawerSaveStartingCountRequest } from "../../generated/dto/CashDrawerSaveStartingCountRequest";
import type { CashDrawerSearchParams } from "../../generated/dto/CashDrawerSearchParams";
import type { CashDrawerStaff } from "../../generated/dto/CashDrawerStaff";
import type { CashDrawerStaffSearchResult } from "../../generated/dto/CashDrawerStaffSearchResult";
import type { CashDrawerTotalFetchOptions } from "../../generated/dto/CashDrawerTotalFetchOptions";
import type { CashDrawerTotalRef } from "../../generated/dto/CashDrawerTotalRef";
import type { CashDrawerTotalStaff } from "../../generated/dto/CashDrawerTotalStaff";
import type { CreateCashDrawer } from "../../generated/dto/CreateCashDrawer";
import type { DeleteCashDrawerChangeStaff } from "../../generated/dto/DeleteCashDrawerChangeStaff";
import type { DeleteCashDrawerStaff } from "../../generated/dto/DeleteCashDrawerStaff";
import type { DeleteCashDrawerTotalStaff } from "../../generated/dto/DeleteCashDrawerTotalStaff";
import type { PrinterFetchOptions } from "../../generated/dto/PrinterFetchOptions";
import type { PrinterStaff } from "../../generated/dto/PrinterStaff";
import type { UpdateCashDrawer } from "../../generated/dto/UpdateCashDrawer";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class CashDrawerApiClient implements CashDrawerApi {
  constructor(private readonly client = useClient()) {}

  async getCashDrawerStaff(id: string, options?: CashDrawerFetchOptions): Promise<CashDrawerStaff> {
    const { data } = await this.client.get<unknown>(`/cashdrawer/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CashDrawerFetchOptions") });
    return deserializeCbiDto<CashDrawerStaff>(data, "generated.dto.CashDrawerStaff");
  }

  async getCashDrawerChangeStaff(id: number, options?: CashDrawerChangeFetchOptions): Promise<CashDrawerChangeStaff> {
    const { data } = await this.client.get<unknown>(`/cashdrawerchange/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CashDrawerChangeFetchOptions") });
    return deserializeCbiDto<CashDrawerChangeStaff>(data, "generated.dto.CashDrawerChangeStaff");
  }

  async getCashDrawerTotalStaff(id: number, options?: CashDrawerTotalFetchOptions): Promise<CashDrawerTotalStaff> {
    const { data } = await this.client.get<unknown>(`/cashdrawertotal/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CashDrawerTotalFetchOptions") });
    return deserializeCbiDto<CashDrawerTotalStaff>(data, "generated.dto.CashDrawerTotalStaff");
  }

  async createCashDrawerStaff(source: CreateCashDrawer): Promise<CashDrawerStaff> {
    const { data } = await this.client.post<unknown>(`/cashdrawer/staff`, serializeCbiDto(source, "generated.dto.CreateCashDrawer"));
    return deserializeCbiDto<CashDrawerStaff>(data, "generated.dto.CashDrawerStaff");
  }

  async updateCashDrawerStaff(source: UpdateCashDrawer): Promise<CashDrawerStaff> {
    const { data } = await this.client.put<unknown>(`/cashdrawer/staff/${source.cashDrawerId}`, serializeCbiDto(source, "generated.dto.UpdateCashDrawer"));
    return deserializeCbiDto<CashDrawerStaff>(data, "generated.dto.CashDrawerStaff");
  }

  async deleteCashDrawerStaff(source: DeleteCashDrawerStaff): Promise<void> {
    await this.client.delete(`/cashdrawer/staff/${source.cashDrawerId}`);
  }

  async deleteCashDrawerChangeStaff(source: DeleteCashDrawerChangeStaff): Promise<void> {
    await this.client.delete(`/cashdrawerchange/staff/${source.assignId}`);
  }

  async deleteCashDrawerTotalStaff(source: DeleteCashDrawerTotalStaff): Promise<void> {
    await this.client.delete(`/cashdrawertotal/staff/${source.totalId}`);
  }

  async searchCashDrawerStaff(params: CashDrawerSearchParams, page: number, perPage: number): Promise<PaginationResult<CashDrawerStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/cashdrawer/staff/search`, serializeCbiDto(params, "generated.dto.CashDrawerSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<CashDrawerStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<CashDrawerStaffSearchResult>>(result.items, "generated.dto.CashDrawerStaffSearchResult") };
    return result;
  }

  async searchCashDrawerStaffFull(params: CashDrawerSearchParams, page: number, perPage: number, options?: CashDrawerFetchOptions): Promise<PaginationResult<CashDrawerStaff>> {
    const { data } = await this.client.post<unknown>(`/cashdrawer/staff/search/full`, serializeCbiDto(params, "generated.dto.CashDrawerSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.CashDrawerFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<CashDrawerStaff>;
    result = { ...result, items: deserializeCbiDto<Array<CashDrawerStaff>>(result.items, "generated.dto.CashDrawerStaff") };
    return result;
  }

  async getCashDrawerStaffPrinter(id: string, options?: PrinterFetchOptions): Promise<PrinterStaff | null> {
    const { data } = await this.client.get<unknown>(`/cashdrawer/staff/${id}/printer`, { params: serializeCbiDto(options, "generated.dto.PrinterFetchOptions") });
    return deserializeCbiDto<PrinterStaff | null>(data, "generated.dto.PrinterStaff");
  }

  async getCashDrawerStaffChanges(id: string, options?: CashDrawerChangeFetchOptions): Promise<Array<CashDrawerChangeStaff>> {
    const { data } = await this.client.get<unknown>(`/cashdrawer/staff/${id}/changes`, { params: serializeCbiDto(options, "generated.dto.CashDrawerChangeFetchOptions") });
    return deserializeCbiDto<Array<CashDrawerChangeStaff>>(data, "generated.dto.CashDrawerChangeStaff");
  }

  async getCashDrawerStaffTotals(id: string, options?: CashDrawerTotalFetchOptions): Promise<Array<CashDrawerTotalStaff>> {
    const { data } = await this.client.get<unknown>(`/cashdrawer/staff/${id}/totals`, { params: serializeCbiDto(options, "generated.dto.CashDrawerTotalFetchOptions") });
    return deserializeCbiDto<Array<CashDrawerTotalStaff>>(data, "generated.dto.CashDrawerTotalStaff");
  }

  async getCashDrawerChangeStaffCashDrawer(id: number, options?: CashDrawerFetchOptions): Promise<CashDrawerStaff | null> {
    const { data } = await this.client.get<unknown>(`/cashdrawerchange/staff/${id}/cashdrawer`, { params: serializeCbiDto(options, "generated.dto.CashDrawerFetchOptions") });
    return deserializeCbiDto<CashDrawerStaff | null>(data, "generated.dto.CashDrawerStaff");
  }

  async getCashDrawerTotalStaffCashDrawer(id: number, options?: CashDrawerFetchOptions): Promise<CashDrawerStaff | null> {
    const { data } = await this.client.get<unknown>(`/cashdrawertotal/staff/${id}/cashdrawer`, { params: serializeCbiDto(options, "generated.dto.CashDrawerFetchOptions") });
    return deserializeCbiDto<CashDrawerStaff | null>(data, "generated.dto.CashDrawerStaff");
  }

  async addCashStaff(request: CashDrawerAddCashRequest): Promise<CashDrawerChangeRef> {
    const { data } = await this.client.post<unknown>(`/cashdrawer/staff/addcash`, serializeCbiDto(request, "generated.dto.CashDrawerAddCashRequest"));
    return deserializeCbiDto<CashDrawerChangeRef>(data, "generated.dto.CashDrawerChangeRef");
  }

  async dailyTotalsStaff(date: Date): Promise<Array<CashDrawerTotalRef>> {
    const { data } = await this.client.post<unknown>(`/cashdrawer/staff/dailytotals`, date);
    return deserializeCbiDto<Array<CashDrawerTotalRef>>(data, "generated.dto.CashDrawerTotalRef");
  }

  async dayChangesStaff(request: CashDrawerDayChangesRequest): Promise<Array<CashDrawerChangeRef>> {
    const { data } = await this.client.post<unknown>(`/cashdrawer/staff/daychanges`, serializeCbiDto(request, "generated.dto.CashDrawerDayChangesRequest"));
    return deserializeCbiDto<Array<CashDrawerChangeRef>>(data, "generated.dto.CashDrawerChangeRef");
  }

  async finalizeStaff(total: CashDrawerTotalRef): Promise<void> {
    await this.client.post(`/cashdrawer/staff/finalize`, serializeCbiDto(total, "generated.dto.CashDrawerTotalRef"));
  }

  async openDayStaff(request: CashDrawerOpenDayRequest): Promise<CashDrawerTotalRef> {
    const { data } = await this.client.post<unknown>(`/cashdrawer/staff/openday`, serializeCbiDto(request, "generated.dto.CashDrawerOpenDayRequest"));
    return deserializeCbiDto<CashDrawerTotalRef>(data, "generated.dto.CashDrawerTotalRef");
  }

  async popStaff(drawer: CashDrawerRef): Promise<void> {
    await this.client.post(`/cashdrawer/staff/pop`, serializeCbiDto(drawer, "generated.dto.CashDrawerRef"));
  }

  async printTestStaff(drawer: CashDrawerRef): Promise<string> {
    const { data } = await this.client.post<unknown>(`/cashdrawer/staff/printtest`, serializeCbiDto(drawer, "generated.dto.CashDrawerRef"));
    return data as string;
  }

  async removeCashStaff(request: CashDrawerRemoveCashRequest): Promise<CashDrawerChangeRef> {
    const { data } = await this.client.post<unknown>(`/cashdrawer/staff/removecash`, serializeCbiDto(request, "generated.dto.CashDrawerRemoveCashRequest"));
    return deserializeCbiDto<CashDrawerChangeRef>(data, "generated.dto.CashDrawerChangeRef");
  }

  async saveCountStaff(request: CashDrawerSaveCountRequest): Promise<void> {
    await this.client.post(`/cashdrawer/staff/savecount`, serializeCbiDto(request, "generated.dto.CashDrawerSaveCountRequest"));
  }

  async saveStartingCountStaff(request: CashDrawerSaveStartingCountRequest): Promise<void> {
    await this.client.post(`/cashdrawer/staff/savestartingcount`, serializeCbiDto(request, "generated.dto.CashDrawerSaveStartingCountRequest"));
  }
}

export const useCashDrawerApi = (): CashDrawerApi => new CashDrawerApiClient();
